// src/components/editor/CommentsPanel.jsx
//
// Collab-tab comment index: every comment on the draft, grouped by chapter and
// then by the line it was made on. Selecting a line scrolls the editor to it and
// drives which comment cards the centre pane shows.
//
// Read state drives the dimming in the mock:
//   - a LINE row dims once every comment on it has been read by this viewer
//   - a CHAPTER heading dims once every comment under it has been read
// `read` is per-viewer (see comments.py list_comments), so one collaborator
// reading a comment never dims it for anybody else.
//
// Each chapter collapses. Its starting state is decided ONCE, the first time
// the chapter appears: open if anything in it is unread, closed if not. It is
// deliberately not re-derived as read state changes — opening a line marks it
// read, so a live rule would snap the chapter shut under the reader's cursor.
// Selecting a line from outside the panel (a notification link, a comment just
// posted) opens its chapter and scrolls the row into view, since that line may
// sit in a chapter that started closed.
//
// NOTE: the panel container (.sidebar-panel-vertical) ships aggressive global
// CSS that forces any MUI Button/IconButton to full width with gold styling, so
// controls here are plain <Box> elements those rules don't target — same
// approach NavigationPanel takes.
//
// Skinned in the app's purple/gold system. The mock is cream/white, but mocks
// are treated as layout only (same rule EditorRightRail documents).
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { groupComments } from '../../utils/commentGrouping';
import { tokens } from '../../theme';
import CommentList from '../comments/CommentList';

const GOLD = '#F5D76E';
const PURPLE = '#5a4d99';
const DIM = 'rgba(255,255,255,0.38)';   // fully-read rows
const LIVE = '#FFFFFF';                 // rows with unread comments

// Compact pill toggle. Hand-rolled rather than MUI <Switch> so the panel's
// global button CSS can't restyle it and so it matches the mock's small pill.
function PillToggle({ checked, onChange, label }) {
  return (
    <Box
      component="label"
      title={label}
      onClick={(e) => { e.preventDefault(); onChange?.(!checked); }}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', flexShrink: 0 }}
    >
      <Typography
        component="span"
        sx={{ fontSize: 9, lineHeight: 1.15, textAlign: 'right', color: 'rgba(255,255,255,0.7)' }}
      >
        Include<br />Unread
      </Typography>
      <Box
        role="switch"
        aria-checked={checked}
        aria-label={label}
        sx={{
          position: 'relative', width: 26, height: 14, flexShrink: 0,
          borderRadius: '20px',
          backgroundColor: checked ? GOLD : 'rgba(255,255,255,0.25)',
          transition: 'background-color 0.15s ease',
        }}
      >
        <Box sx={{
          position: 'absolute', top: 2, left: checked ? 14 : 2,
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: checked ? PURPLE : '#FFFFFF',
          transition: 'left 0.15s ease',
        }} />
      </Box>
    </Box>
  );
}

// Composer for a comment on the passage the author just selected. Appears at
// the top of the panel (not under a line) because the comment doesn't belong to
// a line row yet — it's about to create one.
function PassageComposer({ quote, onSubmit, onCancel }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset between passages so a half-written note can't leak onto the next one.
  useEffect(() => { setText(''); setBusy(false); }, [quote]);

  const submit = async () => {
    const value = text.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      await onSubmit?.(value);
      setText('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ m: 1, p: 1.5, borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.18)' }}>
      <Typography sx={{ fontSize: 10, color: GOLD, fontWeight: 700, letterSpacing: '.06em', mb: 0.75 }}>
        NEW COMMENT
      </Typography>
      {quote && (
        <Typography
          sx={{
            fontSize: 11, color: 'rgba(255,255,255,0.75)', fontStyle: 'italic',
            borderLeft: `2px solid ${GOLD}`, pl: 1, mb: 1,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          “{quote}”
        </Typography>
      )}
      <TextField
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a comment on this passage…"
        multiline
        minRows={2}
        fullWidth
        size="small"
        autoFocus
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter for a new line, as in every chat box.
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
        }}
        sx={{
          mb: 1,
          '& .MuiInputBase-root': { backgroundColor: '#fff', borderRadius: '6px' },
          '& .MuiInputBase-input': { fontSize: 12.5, color: '#1A1A1A' },
        }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box
          component="button" type="button" onClick={submit} disabled={busy || !text.trim()}
          sx={{
            font: 'inherit', fontSize: 12, fontWeight: 700, px: 2, py: 0.75,
            borderRadius: '6px', border: 'none', color: PURPLE, backgroundColor: GOLD,
            cursor: busy || !text.trim() ? 'default' : 'pointer',
            opacity: busy || !text.trim() ? 0.5 : 1,
          }}
        >
          {busy ? 'Posting…' : 'Comment'}
        </Box>
        <Box
          component="button" type="button" onClick={onCancel}
          sx={{
            font: 'inherit', fontSize: 12, px: 2, py: 0.75, borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.35)', color: '#fff',
            background: 'transparent', cursor: 'pointer',
          }}
        >
          Cancel
        </Box>
      </Box>
    </Box>
  );
}

export default function CommentsPanel({
  comments = [],
  outline = [],
  selectedBlockId = null,
  onSelectLine,
  // Clicking the open line again closes it. Without this the only way to put
  // a line's comments away was to open a different line.
  onDeselectLine,
  includeUnread = true,
  onToggleIncludeUnread,
  loading = false,
  error = '',
  lineNumberFor,
  chapterFor,
  // Wiring for the inline cards + composer that replaced the overlay.
  bookId,
  itemType = 'draft',
  currentUserId,
  // Gate the owner-only card actions -- see CommentCard for why these are
  // three separate permissions rather than one.
  isAdmin = false,
  isItemOwner = false,
  collaborators = [],
  onCommentsChanged,
  // { quote, blockId, ... } while the author is writing a comment on a passage.
  pendingComment = null,
  onSubmitComment,
  onCancelComment,
  // chapter key -> open?, owned by the caller so it outlives this panel
  // unmounting (People tab, closing the sidebar). The setter must accept an
  // updater function, as a useState setter does. Both or neither: with only
  // one, the panel uses local state.
  openChapters,
  onOpenChaptersChange,
  // Bumped by the caller on every deliberate selection (a line clicked, a
  // highlight clicked, a comment posted) so re-selecting the SAME line still
  // reopens a chapter the reader had collapsed.
  revealSeq = 0,
  // Caller-owned memory of the last reveal handled. Without it a remount
  // would treat the standing selection as new and reopen a chapter the reader
  // closed. Falls back to a local ref.
  revealedRef: revealedRefProp,
}) {
  const [ownOpenChapters, setOwnOpenChapters] = useState(() => new Map());
  const controlled = !!(openChapters && onOpenChaptersChange);
  const openMap = controlled ? openChapters : ownOpenChapters;
  const setOpenMap = controlled ? onOpenChaptersChange : setOwnOpenChapters;
  const scrollRef = useRef(null);
  const ownRevealedRef = useRef(null);
  // "seq|blockId|chapterKey" last revealed, so a re-render doesn't undo the
  // reader collapsing the chapter that holds the selected line.
  const revealedRef = revealedRefProp || ownRevealedRef;
  const pendingScrollRef = useRef(null);

  const visible = useMemo(() => {
    if (includeUnread) return comments;
    // Literal reading of the ticket's "Include Unread" control: when it's off,
    // unread comments are excluded from the index.
    return (comments || []).filter((c) => c.read);
  }, [comments, includeUnread]);

  const groups = useMemo(
    () => groupComments(visible, outline, lineNumberFor, chapterFor),
    [visible, outline, lineNumberFor, chapterFor],
  );

  const total = visible.length;

  const isOpen = (group) => (openMap.has(group.key) ? openMap.get(group.key) : !group.allRead);

  // Freeze each chapter's starting state the first time it appears.
  useEffect(() => {
    const unseen = groups.filter((g) => !openMap.has(g.key));
    if (unseen.length === 0) return;
    setOpenMap((prev) => {
      const next = new Map(prev);
      unseen.forEach((g) => { if (!next.has(g.key)) next.set(g.key, !g.allRead); });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Open the chapter holding the selected line. Re-checked as groups change
  // because a deep-linked line can land before the chapter map is measured.
  useEffect(() => {
    if (!selectedBlockId) { revealedRef.current = null; return; }
    const group = groups.find((g) => g.lines.some((l) => l.blockId === selectedBlockId));
    if (!group) return;
    const pair = `${revealSeq}|${selectedBlockId}|${group.key}`;
    if (revealedRef.current === pair) return;
    revealedRef.current = pair;
    pendingScrollRef.current = selectedBlockId;
    setOpenMap((prev) => (prev.get(group.key) === true ? prev : new Map(prev).set(group.key, true)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockId, revealSeq, groups]);

  // Bring a revealed row into view once its chapter has rendered open. Scrolls
  // the list itself rather than scrollIntoView, which would also move the page.
  useEffect(() => {
    const id = pendingScrollRef.current;
    const box = scrollRef.current;
    if (!id || !box) return;
    const row = box.querySelector(`[data-line-block="${id}"]`);
    if (!row) return;
    pendingScrollRef.current = null;
    const boxRect = box.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.top < boxRect.top || rowRect.bottom > boxRect.bottom) {
      box.scrollTop += rowRect.top - boxRect.top - 8;
    }
  });

  // Closing a chapter also closes its open line, so the manuscript highlight
  // and the commenter rail don't keep pointing at a comment the reader just
  // put away.
  const holdsSelection = (group) => !!selectedBlockId
    && group.lines.some((l) => l.blockId === selectedBlockId);

  const toggleChapter = (group) => {
    if (isOpen(group) && holdsSelection(group)) onDeselectLine?.();
    setOpenMap((prev) => {
      const open = prev.has(group.key) ? prev.get(group.key) : !group.allRead;
      return new Map(prev).set(group.key, !open);
    });
  };

  const anyOpen = groups.some(isOpen);
  const setAllChapters = (open) => {
    if (!open && groups.some(holdsSelection)) onDeselectLine?.();
    setOpenMap((prev) => {
      const next = new Map(prev);
      groups.forEach((g) => next.set(g.key, open));
      return next;
    });
  };
  const showGroups = !loading && !error && groups.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', color: 'white', height: '100%', overflow: 'hidden' }}>
      {/* No title or back arrow: the tab bar above already names this view, and
          the roster is a peer reachable from there rather than "back". All
          that's left is the list's own filter. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.5, pt: 0.5, pb: 1, flexShrink: 0 }}>
        {showGroups ? (
          <Box
            component="button" type="button"
            onClick={() => setAllChapters(!anyOpen)}
            sx={{
              font: 'inherit', fontSize: 11, p: 0, border: 'none', background: 'transparent',
              color: tokens.color.onDark, cursor: 'pointer',
              '&:hover': { color: tokens.color.goldLight, textDecoration: 'underline' },
              '&:focus-visible': { outline: `2px solid ${tokens.color.goldFlat}`, outlineOffset: 2 },
            }}
          >
            {anyOpen ? 'Collapse all' : 'Expand all'}
          </Box>
        ) : <span />}
        <PillToggle
          checked={includeUnread}
          onChange={onToggleIncludeUnread}
          label="Include unread comments"
        />
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 2 }}>
        {pendingComment && (
          <PassageComposer
            quote={pendingComment.quote}
            onSubmit={onSubmitComment}
            onCancel={onCancelComment}
          />
        )}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 2 }}>
            <CircularProgress size={14} sx={{ color: GOLD }} />
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Loading comments…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{ fontSize: 12, color: '#ff9a9a', px: 1, py: 2, lineHeight: 1.5 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && total === 0 && (
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, px: 1, py: 2, lineHeight: 1.6 }}>
            {comments.length > 0
              ? 'No read comments. Turn on Include Unread to see the rest.'
              : 'No comments yet. Select text in your manuscript and add a comment, or invite a reviewer.'}
          </Typography>
        )}

        {showGroups && groups.map((group) => {
          const open = isOpen(group);
          const label = `${group.chapterNo ? `Ch. ${group.chapterNo} · ` : ''}${group.title}`;
          const chapterCount = group.lines.reduce((n, l) => n + l.comments.length, 0);
          const regionId = `comments-chapter-${group.key}`;
          return (
          <Box key={group.key} sx={{ mb: 0.5 }}>
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={open}
              aria-controls={regionId}
              aria-label={`${label}, ${chapterCount} ${chapterCount === 1 ? 'comment' : 'comments'}${group.allRead ? '' : ', unread'}`}
              onClick={() => toggleChapter(group)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleChapter(group); }
              }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, pt: 1.25, pb: 0.75, mx: 0.5, borderRadius: '6px', cursor: 'pointer',
                // Chapter dims once everything under it has been read -- to
                // `onDark`, not the line rows' fainter DIM: this is a control
                // now, and onDark is the theme's legibility floor on purple.
                color: group.allRead ? tokens.color.onDark : tokens.color.goldLight,
                '&:hover': { backgroundColor: 'rgba(245,215,110,0.15)' },
                '&:focus-visible': { outline: `2px solid ${tokens.color.goldFlat}`, outlineOffset: 1 },
              }}
            >
              {open
                ? <ExpandMoreIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                : <ChevronRightIcon sx={{ fontSize: 16, flexShrink: 0 }} />}
              <Typography noWrap title={label} sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'inherit' }}>
                {label}
              </Typography>
              {!group.allRead && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.color.goldLight, flexShrink: 0 }} />
              )}
              <Typography sx={{ fontSize: 10, color: tokens.color.onDark, flexShrink: 0 }}>
                {chapterCount}
              </Typography>
            </Box>

            {open && (
            <Box id={regionId} sx={{ ml: 2 }}>
            {group.lines.map((line) => {
              const isSelected = selectedBlockId && line.blockId === selectedBlockId;
              const count = line.comments.length;
              const toggleLine = () => {
                if (isSelected && onDeselectLine) onDeselectLine();
                else onSelectLine?.(line);
              };
              const lineColor = isSelected ? PURPLE : (line.allRead ? DIM : LIVE);
              return (
                <React.Fragment key={line.key}>
                  <Box
                    onClick={toggleLine}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLine(); }
                    }}
                    role="button"
                    tabIndex={0}
                    data-line-block={line.blockId || undefined}
                    aria-expanded={!!isSelected}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.5, py: 1, mx: 0.5, borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: isSelected ? GOLD : 'transparent',
                      '&:hover': { backgroundColor: isSelected ? GOLD : 'rgba(245,215,110,0.15)' },
                      '&:focus-visible': { outline: `2px solid ${tokens.color.goldFlat}`, outlineOffset: 1 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: 13,
                      fontWeight: line.allRead ? 400 : 600,
                      color: lineColor,
                    }}>
                      {line.lineNo != null ? `Line ${line.lineNo}` : 'Unplaced'}
                    </Typography>
                    <Typography sx={{
                      fontSize: 10,
                      color: isSelected ? 'rgba(0,0,0,0.6)' : (line.allRead ? DIM : 'rgba(255,255,255,0.6)'),
                    }}>
                      {count} {count === 1 ? 'comment' : 'comments'}
                    </Typography>
                    </Box>
                    {/* Same open/closed cue as the chapter headers, so it reads
                        as something that closes again. */}
                    {isSelected
                      ? <ExpandMoreIcon sx={{ fontSize: 16, flexShrink: 0, color: lineColor }} />
                      : <ChevronRightIcon sx={{ fontSize: 16, flexShrink: 0, color: lineColor }} />}
                  </Box>

                  {/* The merge: a selected line's comments open right here,
                      instead of in an overlay that covered this list. The gold
                      rule keeps them visually tied to the line they're about. */}
                  {isSelected && (
                    <Box sx={{
                      ml: 1.5, mr: 0.5, mt: 0.75, mb: 1,
                      pl: 1.25, borderLeft: `2px solid ${GOLD}`,
                    }}>
                      <CommentList
                        comments={line.comments}
                        bookId={bookId}
                        itemType={itemType}
                        currentUserId={currentUserId}
                        isAdmin={isAdmin}
                        isItemOwner={isItemOwner}
                        collaborators={collaborators}
                        onChanged={onCommentsChanged}
                        twoUp
                      />
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
            </Box>
            )}
          </Box>
          );
        })}
      </Box>

    </Box>
  );
}
